// services/school-ui/src/pages/chat/attachments/ConversationAttachmentAdapter.ts
//
// Custom AttachmentAdapter implementation backed by the conversation-
// scoped attachment endpoints (nova-os#434). The three built-in
// assistant-ui adapters (SimpleImage / SimpleText / Composite) are
// browser-only with no server persistence; we need server-backed refs
// that survive history reload.
//
// Lifecycle: add() → user picks files → POST /v1/conversations/.../
// attachments → AttachmentRef. remove() → DELETE same endpoint, idempotent.
// send() → no-op transform; the canonical refToParts mapping ran in add().

import type {
  Attachment,
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment,
} from "@assistant-ui/core";
import {
  uploadAttachment,
  deleteAttachment,
  AttachmentApiError,
} from "./attachmentsApi";
import { saveFileToMyFiles } from "./promoteToLibrary";
import { MHTML_EXTS, mhtmlFileToHtmlFile } from "../../../lib/mhtml";
import type { AttachmentCapabilities, AttachmentRef } from "./types";
import { registerRef, registerKernelId, lookupKernelId } from "./refRegistry";
import { createClientUuid } from "@/clientId";

interface MakeArgs {
  conversationId: string;
  authHeader: Record<string, string>;
  capabilities: AttachmentCapabilities;
}

export function makeConversationAttachmentAdapter(
  args: MakeArgs,
): AttachmentAdapter {
  const { conversationId, authHeader, capabilities } = args;

  return {
    // 扩展名兜底:.mhtml 的浏览器 mime 因平台而异(message/rfc822 /
    // application/x-mimearchive / multipart/related),accept 用扩展名
    // 更可靠;服务端白名单仍按 mime 校验。
    accept: [...capabilities.allowed_mime_types, ".mht", ".mhtml", ".html"].join(","),

    async *add({ file }: { file: File }) {
      // Pre-validate size locally so the user gets immediate feedback
      // without a round-trip. The kernel re-validates server-side
      // (defense in depth).
      if (file.size > capabilities.max_size_bytes) {
        yield {
          id: createClientUuid(),
          type: "file" as const,
          name: file.name,
          contentType: file.type,
          file,
          status: { type: "incomplete", reason: "error" } as const,
        };
        return;
      }

      const pending: PendingAttachment = {
        id: createClientUuid(),
        type: file.type.startsWith("image/") ? "image" : "file",
        name: file.name,
        contentType: file.type,
        file,
        status: { type: "running", reason: "uploading", progress: 0 },
      };
      yield pending;

      try {
        const ref = await uploadAttachment(authHeader, conversationId, file);
        registerRef(ref);
        // Keep the chip id STABLE (pending.id) across yields. Switching it
        // to ref.id here made assistant-ui treat the success yield as a
        // brand-new attachment: the original clientUuid chip never got its
        // terminal update and stuck at "uploading 0%", while the ref.id
        // chip showed "ready" — two chips per single upload. The kernel id
        // is stashed for remove()/delete via the side-lookup instead.
        registerKernelId(pending.id, ref.id);
        // Auto-persist the upload into the teacher's 我的文件 library
        // (customer request 2026-07-16). Fire-and-forget: a documents-store
        // failure must not fail the chat attachment, and the chip goes
        // "ready" immediately off the conversation-attachment upload above.
        // Only fires on a successful conversation upload, so the error-retry
        // path can't double-save.
        void (async () => {
          // mhtml 镜像前转成独立 html:supernova 只认 html,不转的话
          // 入库不可检索,AI 在聊天里就"读不到"这份附件(Jarod 2026-08-25)。
          let mirror = file;
          const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
          if (MHTML_EXTS.includes(ext)) {
            try {
              mirror = await mhtmlFileToHtmlFile(file);
            } catch {
              /* 解析失败就存原件(至少可下载) */
            }
          }
          await saveFileToMyFiles(authHeader, mirror);
        })().catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[attachments] auto-save to 我的文件 failed:", err);
        });
        yield {
          ...pending,
          status: { type: "requires-action", reason: "composer-send" },
          // Pre-build the ThreadUserMessagePart shape; send() lifts it
          // verbatim to a CompleteAttachment.content array.
          content: refToParts(ref),
        };
      } catch (e) {
        // Swallow the AttachmentApiError details; the chip renders the
        // localized "errorUnknown" copy. Specific 413/415 cases get
        // dedicated chip copy in a future refinement — out of scope v1.
        void (e instanceof AttachmentApiError ? e : undefined);
        yield {
          ...pending,
          status: { type: "incomplete", reason: "error" },
        };
      }
    },

    async remove(attachment: Attachment): Promise<void> {
      // Only delete attachments the kernel knows about (i.e., those
      // whose id came from the upload response). Locally-failed uploads
      // never reached the kernel, so there's nothing to delete server-
      // side; just dropping the chip in the composer is sufficient.
      // We approximate kernel-known by checking whether the attachment
      // carries `content` (set only by the success branch).
      const looksKernelKnown = !!(attachment as { content?: unknown[] }).content;
      if (!looksKernelKnown) return;
      // The chip id is the stable client id (see add()); resolve the
      // kernel-assigned id for the DELETE. Fall back to attachment.id for
      // history-loaded chips, whose id already IS the kernel id.
      const kernelId = lookupKernelId(attachment.id) ?? attachment.id;
      try {
        await deleteAttachment(authHeader, conversationId, kernelId);
      } catch {
        // Best-effort; the user already moved on.
      }
    },

    async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
      return {
        ...attachment,
        status: { type: "complete" },
        content: attachment.content ?? [],
      };
    },
  };
}

export function refToParts(ref: AttachmentRef) {
  // Library treats attachment content as ThreadUserMessageParts.
  // Image parts let vision-capable models ingest the bytes; everything
  // else surfaces as a file part carrying the auth-scoped download_url
  // for the agent to fetch server-side.
  if (ref.mime_type.startsWith("image/")) {
    return [{ type: "image" as const, image: ref.download_url }];
  }
  return [
    {
      type: "file" as const,
      mimeType: ref.mime_type,
      data: ref.download_url,
    },
  ];
}
